import { calcWorkHours, formatDisplayHours, toHm } from '../../../../utils/employeeTimeEntryHours';

export type LeadProjectTimeEntry = {
  id: string;
  employee_id: string;
  employee_name: string;
  /** Null for project time without an appointment (`lead_id` set instead). */
  activity_id: string | null;
  activity_title: string;
  lead_id?: string | null;
  entry_date: string;
  start_time: string | null;
  end_time: string | null;
  break_minutes: number;
  note?: string;
  /** clock | manual | request | absence; null for older rows. */
  source?: string | null;
  edited_at?: string | null;
};

/** Appointment of the project with its assigned employees (for the Ist-time dialog). */
export type LeadTimeActivityOption = {
  id: string;
  title: string;
  due_at: string | null;
  employee_ids: string[];
};

/** Short marker for Ist times that were not stamped as-is: typed by hand, requested, or edited. */
/** Imported rows belong to the source system and cannot be edited here. */
export function isImportedTimeEntry(row: { source?: string | null }): boolean {
  return row.source === 'meinshk';
}

export function istEntryBadge(row: { source?: string | null; edited_at?: string | null }): string | null {
  if (row.source === 'meinshk') return 'MeinSHK';
  if (row.source === 'manual') return 'manuell';
  if (row.source === 'request') return 'beantragt';
  if (row.edited_at) return 'bearbeitet';
  return null;
}

/** Project time booked without an appointment. */
export function entriesWithoutActivity(items: LeadProjectTimeEntry[]): LeadProjectTimeEntry[] {
  return items.filter((row) => !row.activity_id);
}

function employeeLabel(name: string | null | undefined): string {
  return String(name ?? '').trim() || 'Mitarbeiter';
}

export function formatIstRange(start: string | null, end: string | null): string {
  const from = toHm(start);
  const to = toHm(end);
  if (from && to) return `${from}–${to}`;
  if (from) return `seit ${from}`;
  return '—';
}

export function formatIstLine(row: LeadProjectTimeEntry): string {
  const range = formatIstRange(row.start_time, row.end_time);
  const name = employeeLabel(row.employee_name);
  const hours = calcWorkHours(row.start_time, row.end_time, row.break_minutes);
  const hoursPart = hours > 0 ? ` · ${formatDisplayHours(hours)} h` : '';
  return `Ist: ${range} · ${name}${hoursPart}`;
}

export function entriesForActivity(
  items: LeadProjectTimeEntry[],
  activityId: string,
  occurrenceIds?: string[],
): LeadProjectTimeEntry[] {
  const ids = new Set(occurrenceIds?.length ? occurrenceIds : [activityId]);
  return items.filter((row) => row.activity_id != null && ids.has(row.activity_id));
}

export function totalWorkHours(items: LeadProjectTimeEntry[]): number {
  return items.reduce(
    (sum, row) => sum + calcWorkHours(row.start_time, row.end_time, row.break_minutes),
    0,
  );
}

export function groupHoursByEmployee(
  items: LeadProjectTimeEntry[],
): { employeeId: string; name: string; hours: number }[] {
  const map = new Map<string, { employeeId: string; name: string; hours: number }>();
  for (const row of items) {
    const name = employeeLabel(row.employee_name);
    const employeeId = row.employee_id || name;
    const hours = calcWorkHours(row.start_time, row.end_time, row.break_minutes);
    const prev = map.get(employeeId);
    if (prev) {
      prev.hours += hours;
      continue;
    }
    map.set(employeeId, { employeeId, name, hours });
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'de'));
}

export type IstWeekEmployee = {
  employeeId: string;
  name: string;
  hours: number;
  /** Entries per `YYYY-MM-DD` of this week. */
  byDate: Record<string, LeadProjectTimeEntry[]>;
};

export type IstWeek = {
  key: string;
  isoYear: number;
  isoWeek: number;
  /** Monday–Sunday of this week as `YYYY-MM-DD`. */
  days: string[];
  hours: number;
  employees: IstWeekEmployee[];
};

function ymdUtc(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** ISO 8601 week, week-year and Monday of a `YYYY-MM-DD` date. */
export function isoWeekOfDate(ymd: string): { isoYear: number; isoWeek: number; monday: string } {
  const d = new Date(`${ymd}T00:00:00Z`);
  const weekday = d.getUTCDay() || 7;
  const monday = new Date(d);
  monday.setUTCDate(d.getUTCDate() - weekday + 1);
  const thursday = new Date(d);
  thursday.setUTCDate(d.getUTCDate() + 4 - weekday);
  const isoYear = thursday.getUTCFullYear();
  const isoWeek = Math.ceil(((thursday.getTime() - Date.UTC(isoYear, 0, 1)) / 86_400_000 + 1) / 7);
  return { isoYear, isoWeek, monday: ymdUtc(monday) };
}

/** Ist times grouped by calendar week (newest first), each with employees × days. */
export function groupIstByWeek(items: LeadProjectTimeEntry[]): IstWeek[] {
  const weeks = new Map<string, IstWeek>();
  for (const row of items) {
    const { isoYear, isoWeek, monday } = isoWeekOfDate(row.entry_date);
    const key = `${isoYear}-${String(isoWeek).padStart(2, '0')}`;
    let week = weeks.get(key);
    if (!week) {
      const start = new Date(`${monday}T00:00:00Z`);
      const days = Array.from({ length: 7 }, (_, i) => {
        const day = new Date(start);
        day.setUTCDate(start.getUTCDate() + i);
        return ymdUtc(day);
      });
      week = { key, isoYear, isoWeek, days, hours: 0, employees: [] };
      weeks.set(key, week);
    }
    const hours = calcWorkHours(row.start_time, row.end_time, row.break_minutes);
    const name = employeeLabel(row.employee_name);
    const employeeId = row.employee_id || name;
    let employee = week.employees.find((e) => e.employeeId === employeeId);
    if (!employee) {
      employee = { employeeId, name, hours: 0, byDate: {} };
      week.employees.push(employee);
    }
    employee.hours += hours;
    (employee.byDate[row.entry_date] ??= []).push(row);
    week.hours += hours;
  }
  for (const week of weeks.values()) week.employees.sort((a, b) => a.name.localeCompare(b.name, 'de'));
  return [...weeks.values()].sort((a, b) => b.key.localeCompare(a.key));
}
